import React, { useEffect, useMemo, useState } from "react";
import SlideOutSidebar from "ui-components/slide-out-sidebar/slideOutSidebar";
import { Formik, FormikProps, Form } from "formik";
import Button, { ButtonThemes } from "ui-components/button/button";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import "./assignDoorDrawer.scss";
import { useAvailableDoors } from "../hooks/useAvailableDoors";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";

interface DoorOption extends XGSSelectOption {
  priority?: number;
  assignedTrailer?: string;
  manifestNumber?: string;
  activity?: string;
  doorTypeKey?: string;
  rawDoorNumber?: string;
}

interface AssignDoorFormValues {
  door: string;
}

interface AssignDoorDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  manifestNumber?: string;
  trailerNumber?: string;
  currentDoorNumber?: string;
  freightType?: string;
  freightStatus?: string;
  originCity?: string;
  destinationTerminalName?: string;
  onSave?: (doorNumber: string, priority?: number) => void;
}

const formatDoorLabel = (doorNumber?: string) => {
  if (!doorNumber) return "-";
  const normalized = doorNumber.toString().toUpperCase();
  return normalized.startsWith("D-") ? normalized : `D-${normalized}`;
};

const AssignDoorDrawer: React.FC<AssignDoorDrawerProps> = ({
  isOpen,
  onClose,
  manifestNumber,
  trailerNumber,
  currentDoorNumber,
  freightType,
  freightStatus,
  originCity,
  destinationTerminalName,
  onSave,
}) => {
  const formRef = React.useRef<FormikProps<AssignDoorFormValues>>(null);
  const { data: availableDoors, isLoading } = useAvailableDoors();
  const [doorNumberFilter, setDoorNumberFilter] = useState<string>("");
  const [doorTypeFilters, setDoorTypeFilters] = useState<XGSSelectOption[]>([]);
  
  // Determine if this is "Assign" or "Change" mode
  const isChangeMode = !!currentDoorNumber;
  const headerTitle = isChangeMode ? "Edit Door" : "Assign Door";
  const actionButtonText = isChangeMode ? "Assign Door" : "Assign Door";

  // Set initial door value if in change mode
  useEffect(() => {
    if (isOpen && currentDoorNumber && formRef.current) {
      formRef.current.setFieldValue("door", formatDoorLabel(currentDoorNumber));
    } else if (isOpen && !currentDoorNumber) {
      // Reset when opening in assign mode
      formRef.current?.setFieldValue("door", "");
    }
  }, [isOpen, currentDoorNumber]);

  useEffect(() => {
    if (isOpen) {
      setDoorNumberFilter("");
      setDoorTypeFilters([]);
    }
  }, [isOpen]);

  const initialValues: AssignDoorFormValues = {
    door: currentDoorNumber ? formatDoorLabel(currentDoorNumber) : "",
  };

  const formatLabel = (value?: string) => {
    if (!value) return "-";
    return value
      .toString()
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const typeLabel = React.useMemo(() => {
    if (freightType) {
      return formatLabel(freightType);
    }
    const origin = originCity?.toString().toUpperCase();
    const destination = destinationTerminalName?.toString().toUpperCase();
    if (!origin || !destination) return "-";
    return origin === destination ? "Local" : "Linehaul";
  }, [freightType, originCity, destinationTerminalName]);

  const doorOptions: DoorOption[] = useMemo(() => {
    if (!availableDoors) return [];
    const mapped = availableDoors.map((door) => {
      const doorTypeKey = door.doorType?.toString().trim().toLowerCase() || "";
      const doorTypeLabel = door.doorType ? formatLabel(door.doorType) : "Unloading";
      const label = formatDoorLabel(door.doorNumber);
      const trailerValue = door.trailerNumber && door.trailerNumber !== "0"
        ? `TRL-${door.trailerNumber}`
        : "-";
      const manifestValue = door.manifestNumber && door.manifestNumber !== "0"
        ? `MF-${door.manifestNumber}`
        : "-";
      const sortRank = doorTypeKey === "unloading" ? 0 : doorTypeKey === "loading" ? 1 : 2;
      return {
        label,
        value: label,
        activity: doorTypeLabel,
        priority: door.priority,
        assignedTrailer: trailerValue,
        manifestNumber: manifestValue,
        doorTypeKey,
        rawDoorNumber: door.doorNumber?.toString() || "",
        sortRank,
      };
    });
    return mapped.sort((a, b) => {
      if ((a as any).sortRank !== (b as any).sortRank) {
        return (a as any).sortRank - (b as any).sortRank;
      }
      const aPriority = typeof a.priority === "number" ? a.priority : Number.MAX_SAFE_INTEGER;
      const bPriority = typeof b.priority === "number" ? b.priority : Number.MAX_SAFE_INTEGER;
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      return a.label.localeCompare(b.label);
    });
  }, [availableDoors]);

  const currentDoorLabel = currentDoorNumber ? formatDoorLabel(currentDoorNumber) : "";
  const currentDoor = currentDoorNumber
    ? doorOptions.find((door) => door.value === currentDoorLabel)
    : undefined;

  const subtitleText = isChangeMode
    ? `Assign a different door for ${currentDoor?.activity || "Unloading"}`
    : `Select an available door to assign to ${trailerNumber ? `TRL-${trailerNumber}` : "this trailer"}`;

  const doorTypeOptions: XGSSelectOption[] = useMemo(() => {
    const types = new Map<string, string>();
    (availableDoors || []).forEach((door) => {
      const key = door.doorType?.toString().trim().toLowerCase() || "";
      if (!key) return;
      if (!types.has(key)) {
        types.set(key, formatLabel(door.doorType));
      }
    });
    return Array.from(types.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [availableDoors]);

  const filteredDoorOptions = useMemo(() => {
    const search = doorNumberFilter.trim().toUpperCase();
    const searchNoPrefix = search.replace(/^D-?/i, "");
    const selectedTypeKeys = doorTypeFilters.map((opt) => opt.value?.toString().toLowerCase());
    return doorOptions.filter((door) => {
      const matchesNumber = !search
        || door.label.toUpperCase().includes(search)
        || (door.rawDoorNumber || "").toUpperCase().includes(searchNoPrefix);
      const matchesType = selectedTypeKeys.length === 0
        || selectedTypeKeys.includes((door.doorTypeKey || "").toLowerCase());
      return matchesNumber && matchesType;
    });
  }, [doorOptions, doorNumberFilter, doorTypeFilters]);

  return (
    <SlideOutSidebar
      header={headerTitle}
      onClose={onClose}
      show={isOpen}
      size="small"
      className="assign-door-drawer-sidebar"
    >
      <div className="assign-door-drawer">
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => {
            const selectedDoor = doorOptions.find((door) => door.value === values.door);
            onSave?.(values.door, selectedDoor?.priority);
            onClose();
          }}
          innerRef={formRef}
          enableReinitialize={false}
        >
          {(props: FormikProps<AssignDoorFormValues>) => (
            <Form>
              <div className="assign-door-drawer__form">
                <div className="assign-door-drawer__subtitle">
                  {subtitleText}
                </div>

                <div className="assign-door-drawer__info">
                  <div className="assign-door-drawer__info-row labels">
                    <div>Trailer</div>
                    <div>Manifest</div>
                    <div>Type</div>
                    <div>Status</div>
                  </div>
                  <div className="assign-door-drawer__info-row values">
                    <div>{trailerNumber ? `TRL-${trailerNumber}` : "-"}</div>
                    <div>{manifestNumber ? `MF-${manifestNumber}` : "-"}</div>
                    <div>{typeLabel}</div>
                    <div>{formatLabel(freightStatus)}</div>
                  </div>
                </div>

                <div className="assign-door-drawer__filters">
                  <LabeledTextInput
                    label="Door number"
                    labelMode={LabelModes.column}
                    value={doorNumberFilter}
                    onChange={(e) => setDoorNumberFilter(e.currentTarget.value)}
                    placeholder="Search door number"
                    clearable
                    onClear={() => setDoorNumberFilter("")}
                  />
                  <LabeledSelectInput
                    label="Door types"
                    labelMode={LabelModes.column}
                    isMulti
                    isClearable
                    placeholder="Select door type"
                    options={doorTypeOptions}
                    value={doorTypeFilters}
                    onMultiValuesChange={(options) => {
                      setDoorTypeFilters(options ?? []);
                    }}
                  />
                </div>

                {isChangeMode && (
                  <div className="assign-door-drawer__section">
                    <div className="assign-door-drawer__section-title">Current Door</div>
                    <div className="assign-door-drawer__door-card is-static">
                      <div className="assign-door-drawer__door-card-header">
                        <div className="assign-door-drawer__door-card-title">
                          <span>{currentDoorLabel || "-"}</span>
                          <span className="assign-door-drawer__door-card-status">
                            {currentDoor?.activity || "Unloading"}
                          </span>
                        </div>
                      </div>
                      <div className="assign-door-drawer__door-card-body">
                        <div>
                          <div className="assign-door-drawer__door-card-label">Priority</div>
                          <div className="assign-door-drawer__door-card-value">{currentDoor?.priority ?? "-"}</div>
                        </div>
                        <div>
                          <div className="assign-door-drawer__door-card-label">Assigned Trailer</div>
                          <div className="assign-door-drawer__door-card-value">
                            {currentDoor?.assignedTrailer || (trailerNumber ? `TRL-${trailerNumber}` : "-")}
                          </div>
                        </div>
                        <div>
                          <div className="assign-door-drawer__door-card-label">Manifest</div>
                          <div className="assign-door-drawer__door-card-value">
                            {currentDoor?.manifestNumber || (manifestNumber ? `MF-${manifestNumber}` : "-")}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {isChangeMode && <div className="assign-door-drawer__divider" />}
                {isChangeMode && (
                  <div className="assign-door-drawer__section-title">Available Doors</div>
                )}

                <div className="assign-door-drawer__door-list">
                  {isLoading && (
                    <div className="assign-door-drawer__subtitle">Loading doors...</div>
                  )}
                  {!isLoading && filteredDoorOptions.length === 0 && (
                    <div className="assign-door-drawer__subtitle">No doors match the selected filters.</div>
                  )}
                  {!isLoading && filteredDoorOptions.map((door) => {
                    const isSelected = props.values.door === door.value;
                    const isCurrentDoor = isChangeMode && door.value === currentDoorLabel;
                    if (isCurrentDoor) {
                      return null;
                    }
                    return (
                      <label
                        key={door.value}
                        className={`assign-door-drawer__door-card ${isSelected ? "is-selected" : ""}`}
                      >
                        <div className="assign-door-drawer__door-card-header">
                          <div className="assign-door-drawer__door-card-title">
                            <input
                              type="radio"
                              name="door"
                              value={door.value}
                              checked={isSelected}
                              onChange={() => props.setFieldValue("door", door.value)}
                            />
                            <span>{door.label}</span>
                            {door.activity && (
                              <span className="assign-door-drawer__door-card-status">
                                {door.activity}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="assign-door-drawer__door-card-body">
                          <div>
                            <div className="assign-door-drawer__door-card-label">Priority</div>
                            <div className="assign-door-drawer__door-card-value">{door.priority ?? "-"}</div>
                          </div>
                          <div>
                            <div className="assign-door-drawer__door-card-label">Assigned Trailer</div>
                            <div className="assign-door-drawer__door-card-value">{door.assignedTrailer || "-"}</div>
                          </div>
                          <div>
                            <div className="assign-door-drawer__door-card-label">Manifest</div>
                            <div className="assign-door-drawer__door-card-value">{door.manifestNumber || "-"}</div>
                          </div>
                        </div>

                        {isSelected && (
                          <div className="assign-door-drawer__door-card-actions">
                            <Button
                              type="submit"
                              theme={ButtonThemes.blue}
                              disabled={!props.values.door}
                            >
                              {actionButtonText}
                            </Button>
                          </div>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </SlideOutSidebar>
  );
};

export default AssignDoorDrawer;
